import React, { useState, useEffect } from 'react';
import './LocalSeoStoreChart.scss';
import translate from "../../../config/translations/translate";

import LocalSeoStoreContent from '../shared/LocalSeoStoreContent';
import { getLocalSeoStoreChartData } from '../../../api/dashboardV2Api';

export const PREDEFINED_COLORS = [
  '#FF5733',  // rojo anaranjado intenso
  '#33FF57',  // verde brillante
  '#33C1FF',  // azul celeste
  '#FFD433',  // amarillo dorado
  '#4B0082',  // índigo
  '#33FFDD',  // turquesa claro
  '#9D33FF',  // violeta
  '#FF8F33',  // naranja
  '#FF33A8',  // rosa fuerte
  '#8DFF33',  // verde lima
  '#3385FF',  // azul clásico 
  '#C70039',  // rojo oscuro
];

const formatDate = (timestamp, intervalPeriod, t) => {
  const date = new Date(timestamp);
  if (['LAST_3_MONTHS', 'LAST_6_MONTHS', 'LAST_12_MONTHS', 'THIS_YEAR'].includes(intervalPeriod)) {
    const monthNames = [
      'february', 'march', 'april', 'may', 'june', 'july',
      'august', 'september', 'october', 'november', 'december', 'january'
    ];
    const monthKey = monthNames[date.getMonth()];
    const month = t(`months.${monthKey}`);
    const year = date.getFullYear();
    
    return month.charAt(0).toUpperCase() + month.slice(1) + ' ' + year;
  }
  
  // Para fechas cortas
  return date.toLocaleDateString(undefined, { 
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const options = (t) => {
  return {
    responsive: true,
    elements: {
      point: {
        radius: 5,
        backgroundColor: function(context) {
          return context.dataset.borderColor;
        },
      },
      line: {
        tension: 0.1
      }
    },
    scales: {
      yAxes: [{
        gridLines: {
          display: false
        },
        scaleLabel: {
          display: true,
          fontStyle: 'bold',
          fontSize: 12,
          fontColor: '#000000',
          fontFamily: 'Open Sans'
        },
        ticks: {
          reverse: true,
          min: 1,
          max: 21,
          stepSize: 3,
          fontSize: 12,
          fontColor: '#000000',
          fontFamily: 'Open Sans'
        }
      }],
      xAxes: [{
        gridLines: {
          display: false
        },
        scaleLabel: {
          display: true,
          fontStyle: 'bold',
          fontSize: 12,
          fontColor: '#000000',
          fontFamily: 'Open Sans'
        },
        ticks: {
          fontStyle: 'bold',
          fontSize: 12,
          fontColor: '#000000',
          fontFamily: 'Open Sans'
        }
      }]
    },
    legend: {
      position: 'top',
      labels: {
        boxWidth: 15,      
        padding: 15,        
        fontSize: 14,         
        fontColor: '#969696',   
        fontFamily: 'Open Sans',
        
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 15,      
          padding: 15,        
          fontSize: 14,         
          fontColor: '#969696',   
          fontFamily: 'Open Sans',
        }
      },
      // Agregar configuración para cuando no hay datos
      noData: {
        text: t('noDataAvailable'),
        align: 'center',
        verticalAlign: 'middle',
        style: {
          fontSize: '16px',
          color: '#666666'
        }
      }
    },
  }
};


export const processChartData = (data, t) => {
    if (!data || !data.rankDates || !data.rankDates.length) {
        return null;
    }

    const { rankDates, keywordsRankingByTimePeriod, intervalPeriod } = data;
    
    return {
        labels: rankDates.map(timestamp => formatDate(timestamp, intervalPeriod, t)),
        datasets: Object.entries(keywordsRankingByTimePeriod).map(([keyword, rankings], index) => ({
            label: keyword === "-1" ? t('averageRanking') : keyword,
            data: rankings.map(value => value !== null ? Number(value.toFixed(1)) : null),
            fill: false,
            borderColor: PREDEFINED_COLORS[index] || 
                `hsl(${index * 30 + (index > 5 ? 15 : 0)}, 85%, ${index % 2 ? 45 : 55}%)`,
            tension: 0.1
        }))
    };
};

const LocalSeoStoreChart = ({
    store, 
    selectedPeriod, 
    t, 
    setPlaceId,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [keywordSearchCounts, setKeywordSearchCounts] = useState([]);

    useEffect(() => {
        if (!store || !store.id) return;
        
        const fetchChartData = async () => {
            setIsLoading(true);
            try {
                const res = await getLocalSeoStoreChartData(store.id, selectedPeriod);
                const processedChartData = processChartData(res.data.data, t);
                setChartData(processedChartData);
                setKeywordSearchCounts(res.data.data.keywordSearchCounts);
                
                if (res.data.data && res.data.data.placeId) {
                    setPlaceId(res.data.data.placeId);
                }
            } catch (error) {
                console.error('Error fetching chart data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchChartData();
    }, [store && store.id, selectedPeriod]);

    if (!store) {
        return (
            <div className="local-seo-chart-wrapper">
                <h2>{t('noLocationSelected')}</h2>
            </div>
        );
    }

    return (
        <div className="local-seo-store-chart">
            <LocalSeoStoreContent 
                store={store}
                isLoading={isLoading}
                chartData={chartData}
                keywordSearchCounts={keywordSearchCounts}
                t={t}
            />
        </div>
    );
};

LocalSeoStoreChart.translations = {
  noLocationSelected: {
    
    es: "Por favor, seleccione una ubicación para ver el gráfico",
    en: "Please select a location to view the chart",
 
  },
  averageRanking: {
    es: "ranking promedio",
    en: "average ranking",
  },
  months: {
    december: {
    
      es: "Diciembre",
      en: "December",
     
    },
    november: {
    
      es: "Noviembre",
      en: "November",
 
    },
    october: {
 
      es: "Octubre",
      en: "October",
 
    },
    september: {
      
      es: "Septiembre",
      en: "September",
      
    },
    august: {
      
      es: "Agosto",
      en: "August",
    
    },
    july: {
     
      es: "Julio",
      en: "July",
   
    },
    june: {

      es: "Junio",
      en: "June",
    
    },
    may: {

      es: "Mayo",
      en: "May",

    },
    april: {
   
      es: "Abril",
      en: "April",
   
    },
    march: {
    
      es: "Marzo",
      en: "March",
     
    },
    february: {
  
      es: "Febrero",
      en: "February",
     
    },
    january: {
      
      es: "Enero",
      en: "January",
     
    }
  },
  noDataAvailable: {
    es: "No hay datos disponibles para esta tienda y período",
    en: "No data available for this store and period",
  },
  searches: {
    es: "búsquedas",
    en: "searches"
  },
  metrics: {
    visits: {
      es: "Visitas",
      en: "Visits"
    },
    walkIns: {
      es: "Visitas a la tienda",
      en: "Walk-ins"
    },
    websiteClicks: {
      es: "Clics al sitio web",
      en: "Website Clicks"
    },
    orders: {
      es: "Pedidos",
      en: "Orders"
    },
    menuClicks: {
      es: "Clics al menú",
      en: "Menu Clicks"
    },
    bookings: {
      es: "Reservas",
      en: "Bookings"
    },
    engagementRate: {
      es: "Tasa de interacción",
      en: "Eng. Rate"
    }
  },
  columns: {
    comparisonTooltip: {
      es: "Esta comparación muestra el porcentaje de cambio entre el período y el mismo período del año anterior.",
      en: "This comparison shows the percent of change between the period and the same period the year before."
    },
    noDataComparisonTooltip: {
      es: "Esta comparación no aplica, ya que no hay datos suficientes para el período anterior.",
      en: "This comparison does not apply, as there is not enough data for the previous period."
    }
  }
};

const TranslatedLocalSeoStoreChart = translate(LocalSeoStoreChart);
export { TranslatedLocalSeoStoreChart as LocalSeoStoreChartComponent };
export default TranslatedLocalSeoStoreChart;


